import React, { useState, useRef } from 'react';
import { useApp } from '@/contexts/AppContext';
import { useStrings } from '@/hooks/useStrings';
import { useScreenTransition } from '@/hooks/useScreenTransition';
import useScreenFocus from '@/hooks/useScreenFocus';
import { safeWrite } from '@/utils/supabaseWrite';
import { hapticTap, hapticMilestone } from '@/utils/haptics';
import { playSessionCompleteChime } from '@/utils/sound';
import OnboardingHeader from '@/components/shared/OnboardingHeader';
import RingPlayer from '@/components/training/RingPlayer';
import { s } from './foundation/styles';
import { FeltSenseCheck } from './foundation/atoms';
import { SCREENS, ACTIVE_SCREENS } from './foundation/screens';

// Re-exported for tests + any caller that wants to launch the lesson
// at a specific step. Source of truth lives in foundation/screens.
export { ACTIVE_SCREENS };
const TOTAL_SCREENS = ACTIVE_SCREENS.length;

// Tryout squeeze exercise launched from the "Begin" CTA on screen 9.
// Matches the brief: 3 × 10 reps, 5s hold, 3s release, 60s rest between
// sets. Runs through the real RingPlayer state machine so the lesson
// ends with the user doing one felt rep before the rest of onboarding
// picks up.
const SQUEEZE_TRY_EXERCISE = Object.freeze({
  id: 'onboarding-squeeze-tryout',
  name: 'Squeeze',
  name_pt: 'Aperto',
  movement: 'squeeze',
  type: 'hold',
  movementMode: 'squeeze',
  sets: 3,
  reps: 10,
  hold: 5,
  relax: 3,
  rest: 60,
  timerCue: 'Hold.\nRelease.',
  timerCue_pt: 'Segure.\nSolte.',
});

// Push (Gentle Push) onboarding tryout. Followed by the fl_correct
// recap once all 3 sets finish. `relax: 6` is intentionally long so
// the descent phase stretches into a slow elevator ride the user can
// feel level by level; RingPlayer scopes its RELEASE transition to
// 2400ms for push exercises so the flower's return-to-rest mirrors
// the breath's pace.
const PUSH_TRY_EXERCISE = Object.freeze({
  id: 'onboarding-push-tryout',
  name: 'Gentle Push',
  name_pt: 'Empurrão Suave',
  movement: 'push',
  type: 'hold',
  movementMode: 'push',
  sets: 3,
  reps: 10,
  hold: 3,
  relax: 6,
  rest: 120,
  breathe: 'Exhale to prepare.',
  breathe_pt: 'Expire para preparar.',
  timerCue: 'Push up.\nNow lower slowly.',
  timerCue_pt: 'Empurre para cima.\nAgora desça devagar.',
});

export default function FoundationLesson({ onComplete, onClose, onExit, initialScreen = 0, revisit = false }) {
  const { user, userProgress, patchProgress, lang } = useApp();
  const t = useStrings();
  const startScreen = ACTIVE_SCREENS.includes(initialScreen) ? initialScreen : ACTIVE_SCREENS[0];
  const [currentScreen, setCurrentScreen] = useState(startScreen);
  const [saving, setSaving] = useState(false);
  // Tryout overlays: launched from screens 9 (squeeze) and 11 (push).
  // Each runs a full RingPlayer set, then advances the lesson to the
  // matching follow-up screen on completion (or back to the notes
  // screen on exit).
  const [showTryout, setShowTryout] = useState(false);
  const [showPushTryout, setShowPushTryout] = useState(false);
  const contentRef = useRef(null);
  useScreenTransition(currentScreen);
  useScreenFocus(contentRef, currentScreen);

  // Read fresh user_path from localStorage (written by PathAssessment).
  // Fall back to userProgress.path, then 'A'.
  function getUserPath() {
    try {
      const saved = localStorage.getItem('bb7');
      if (saved) {
        const data = JSON.parse(saved);
        if (data?.user_path) return data.user_path;
      }
    } catch {}
    return userProgress?.path || 'A';
  }

  const userPath = getUserPath();

  function goNext() {
    // In revisit mode each list card is a standalone lesson, so the
    // forward CTA dismisses the overlay (back to /home via onComplete)
    // instead of walking through every remaining onboarding screen.
    if (revisit) {
      if (onComplete) onComplete();
      return;
    }
    hapticTap();
    setCurrentScreen((id) => {
      const i = ACTIVE_SCREENS.indexOf(id);
      if (i < 0 || i >= ACTIVE_SCREENS.length - 1) return id;
      return ACTIVE_SCREENS[i + 1];
    });
  }

  // goBack doubles as the OnboardingHeader back handler. On the first
  // active screen it dismisses the whole onboarding overlay (→ /home);
  // otherwise it steps back through the active screens so the user can
  // re-read the previous page without losing progress.
  function goBack() {
    if (saving) return;
    if (currentScreen === ACTIVE_SCREENS[0]) {
      if (onClose) onClose();
      return;
    }
    setCurrentScreen((id) => {
      const i = ACTIVE_SCREENS.indexOf(id);
      if (i <= 0) return id;
      return ACTIVE_SCREENS[i - 1];
    });
  }

  // Skip writes a "skipped" flag so reporting can distinguish users
  // who completed the lesson from those who opted out, then advances
  // the outer OnboardingFlow. Educational content only — no gameplay
  // state depends on this.
  async function handleSkip() {
    if (saving) return;
    if (revisit) {
      if (onClose) onClose();
      return;
    }
    setSaving(true);
    try {
      let data = {};
      try {
        const saved = localStorage.getItem('bb7');
        if (saved) data = JSON.parse(saved);
      } catch {}
      data.foundation_lesson_skipped = true;
      await safeWrite(user?.id, data);
      onComplete();
    } catch (err) {
      console.error('FoundationLesson skip save failed:', err);
      setSaving(false);
    }
  }

  // Screen 3 secondary link ("I have pelvic pain, take me to the right
  // lesson"). Path B in the curriculum is the release-first track
  // designed for hypertonic / painful floors, so the safest handling is
  // to switch the user's training path and continue. We patch the
  // in-memory gate immediately so the home screen reflects the change
  // on the same tick, then mirror to Supabase via safeWrite (fire-and-
  // forget — localStorage is the synchronous source of truth, see the
  // patchProgress pattern in CLAUDE.md).
  async function handleSecondaryLink() {
    if (saving) return;
    if (revisit) {
      if (onClose) onClose();
      return;
    }
    setSaving(true);
    patchProgress({ path: 'B' });
    try {
      let data = {};
      try {
        const saved = localStorage.getItem('bb7');
        if (saved) data = JSON.parse(saved);
      } catch {}
      data.user_path = 'B';
      data.path = 'B';
      data.foundation_lesson_complete = true;
      data.foundation_lesson_completed_at = new Date().toISOString();
      await safeWrite(user?.id, data);
      onComplete();
    } catch (err) {
      console.error('FoundationLesson path-B switch failed:', err);
      setSaving(false);
    }
  }

  // Record a per-screen clarity signal. patchProgress flips the
  // in-memory map so the chip stays "selected" if the user steps
  // back to this screen; safeWrite mirrors to Supabase fire-and-
  // forget per the patchProgress pattern in CLAUDE.md (never await
  // — the chip should feel instant even on a slow network).
  function recordSignal(value) {
    if (revisit) return; // No persistence in single-lesson revisit mode.
    const screenId = String(currentScreen);
    const nextSignals = { ...(userProgress?.fl_signals || {}), [screenId]: value };
    patchProgress({ fl_signals: nextSignals });
    try {
      let data = {};
      try {
        const saved = localStorage.getItem('bb7');
        if (saved) data = JSON.parse(saved);
      } catch {}
      data.fl_signals = nextSignals;
      safeWrite(user?.id, data).catch(() => {});
    } catch {}
  }

  async function finishAndContinue() {
    if (saving) return;
    if (revisit) {
      onComplete();
      return;
    }
    hapticMilestone();
    playSessionCompleteChime();
    setSaving(true);
    try {
      let data = {};
      try {
        const saved = localStorage.getItem('bb7');
        if (saved) data = JSON.parse(saved);
      } catch {}
      data.foundation_lesson_complete = true;
      data.foundation_lesson_completed_at = new Date().toISOString();
      await safeWrite(user?.id, data);
      onComplete();
    } catch (err) {
      console.error('FoundationLesson save failed:', err);
      setSaving(false);
    }
  }

  const stepIndex = Math.max(0, ACTIVE_SCREENS.indexOf(currentScreen));
  const progressPct = ((stepIndex + 1) / TOTAL_SCREENS) * 100;

  // Resolve the per-screen module from the screens map. Each module
  // exports Content (body) + Footer (button bar). Footer props vary
  // by screen (path-B switch, tryout launch, finish) so we dispatch
  // on the screen ID rather than passing the full handler bag.
  const screen = SCREENS[currentScreen];

  function renderFooter() {
    if (!screen) return null;
    switch (currentScreen) {
      case 3:
        return (
          <screen.Footer
            t={t}
            onNext={goNext}
            onSecondaryLink={handleSecondaryLink}
            onBack={goBack}
          />
        );
      case 9:
        return (
          <screen.Footer
            t={t}
            onBeginTryout={() => { hapticTap(); setShowTryout(true); }}
          />
        );
      case 11:
        return (
          <screen.Footer
            t={t}
            onBeginTryout={() => { hapticTap(); setShowPushTryout(true); }}
          />
        );
      case 12:
        return (
          <screen.Footer t={t} onFinish={finishAndContinue} saving={saving} />
        );
      default:
        return <screen.Footer t={t} onNext={goNext} />;
    }
  }

  return (
    <div style={s.screen}>
      <OnboardingHeader onBack={goBack} onSkip={handleSkip} onExit={revisit ? null : onExit} />

      {/* Progress bar — hidden in revisit mode because the bar measures
          a linear walk-through, which doesn't make sense when the user
          jumped to a single lesson from the list. */}
      {!revisit && (
        <div
          style={s.progressTrack}
          role="progressbar"
          aria-valuemin={1}
          aria-valuemax={TOTAL_SCREENS}
          aria-valuenow={stepIndex + 1}
        >
          <div style={{ ...s.progressFill, width: `${progressPct}%` }} />
        </div>
      )}

      {/* Screen content */}
      <div style={s.scroll}>
        <div style={s.container} ref={contentRef}>
          {screen && (
            <div key={`screen-${currentScreen}`}>
              <screen.Content t={t} userPath={userPath} />
            </div>
          )}

          {/* Felt-sense check sits at the bottom of the scroll
              container so it lands the same way on every teaching
              screen. Hidden in revisit mode — single-lesson re-watch
              doesn't accumulate fresh signals. Keyed on currentScreen
              so it remounts (resets selection visual + collapses
              "not yet" hint) when the lesson advances. Prefix avoids
              colliding with the screen wrapper's key — duplicate sibling
              keys silently caused React to keep both old screens mounted,
              stacking content on every advance. */}
          {!revisit && (
            <FeltSenseCheck
              key={`check-${currentScreen}`}
              t={t}
              initialValue={userProgress?.fl_signals?.[String(currentScreen)] || null}
              onChange={recordSignal}
            />
          )}
        </div>
      </div>

      {/* Footer CTA */}
      <div style={s.footer}>{renderFooter()}</div>

      {/* Squeeze tryout RingPlayer. onComplete advances to screen 10,
          which now opens the Gentle Push lesson (steps + cards + push
          tryout) before the user reaches the fl_correct recap at
          screen 12. onExit bails back to the Squeeze notes screen (9)
          so the user can bow out without losing their lesson position. */}
      {showTryout && (
        <RingPlayer
          exercise={SQUEEZE_TRY_EXERCISE}
          lang={lang}
          onComplete={() => {
            setShowTryout(false);
            // Revisiting a single squeeze lesson: finishing the tryout
            // returns the user to /home, not deeper into onboarding.
            if (revisit) {
              if (onComplete) onComplete();
              return;
            }
            setCurrentScreen(10);
          }}
          onExit={() => {
            setShowTryout(false);
          }}
        />
      )}

      {/* Push tryout RingPlayer. Shown after the user taps Begin on
          the Push lesson's special-cards screen (11). onComplete
          advances to the fl_correct recap at screen 12; onExit bails
          back to the push cards screen. */}
      {showPushTryout && (
        <RingPlayer
          exercise={PUSH_TRY_EXERCISE}
          lang={lang}
          onComplete={() => {
            setShowPushTryout(false);
            if (revisit) {
              if (onComplete) onComplete();
              return;
            }
            setCurrentScreen(12);
          }}
          onExit={() => {
            setShowPushTryout(false);
          }}
        />
      )}
    </div>
  );
}
